import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createAdNetwork, updateAdNetwork } from '@/api/adNetworkApi';
import { AdNetwork } from '@/types/ad-network';
import { cnpjMask, validaCNPJ } from '@/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface AddModalProps {
    adNetwork: AdNetwork | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: AdNetwork) => void;
    onUpdateData: (newData: AdNetwork) => void;
}

const AddEditAdNetworkModal = ({ adNetwork, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);

    const isEditing = !!adNetwork;

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('O nome é obrigatório'),
        document: Yup.string()
            .test('is-valid-cnpj', 'CNPJ inválido', value => {
                if (!value || value.trim() === '') return true;
                return validaCNPJ(value);
            }),
        site: Yup.string().trim().required('O site é obrigatório'),
        googleNetworkId: Yup.string().trim(),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: adNetwork?.id ? adNetwork.id : '',
            name: adNetwork?.name ? adNetwork.name : '',
            document: adNetwork?.document ? adNetwork.document : '',
            site: adNetwork?.site ? adNetwork.site : '',
            googleNetworkId: adNetwork?.googleNetworkId ? adNetwork.googleNetworkId : '',
        },
        validationSchema,
        onSubmit: (values: any) => {
            setLoading(true);
            if (adNetwork) {
                updateAdNetwork(values)
                    .then((res: any) => {
                        toggle();
                        toast.success('Ad Network atualizada com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createAdNetwork(values)
                    .then((res: any) => {
                        toggle();
                        toast.success('Ad Network cadastrada com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => {})
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggle}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{isEditing ? 'Editar' : 'Adicionar'} ad network</DialogTitle>
                    <DialogDescription>
                        {isEditing ? 'Edite as informações da ad network.' : 'Preencha as informações para cadastrar uma nova ad network.'}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                id="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.name && formik.errors.name ? 'border-destructive' : ''}
                            />
                            {formik.touched.name && formik.errors.name && (
                                <p className="text-sm text-destructive">{formik.errors.name}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="document">Documento (CNPJ)</Label>
                            <Input
                                id="document"
                                value={cnpjMask(formik.values.document)}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.document && formik.errors.document ? 'border-destructive' : ''}
                            />
                            {formik.touched.document && formik.errors.document && (
                                <p className="text-sm text-destructive">{formik.errors.document}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="site">Site</Label>
                            <Input
                                id="site"
                                placeholder="exemplo.com"
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.site && formik.errors.site ? 'border-destructive' : ''}
                            />
                            {formik.touched.site && formik.errors.site && (
                                <p className="text-sm text-destructive">{formik.errors.site}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="googleNetworkId">Google Network ID</Label>
                            <Input
                                id="googleNetworkId"
                                placeholder="123456789"
                                value={formik.values.googleNetworkId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={formik.touched.googleNetworkId && formik.errors.googleNetworkId ? 'border-destructive' : ''}
                            />
                            {formik.touched.googleNetworkId && formik.errors.googleNetworkId && (
                                <p className="text-sm text-destructive">{formik.errors.googleNetworkId}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={toggle}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEditing ? 'Editar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditAdNetworkModal;
